import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import { Breadcrumbs } from '@p81-common/ui';

import { upFirstLetter } from 'utils/common';

// Mirrors BreadcrumbsProps['customBreadcrumbs'] in the @p81-common/ui types.
interface Crumb {
  text: string;
  href: string;
}

interface BreadCrumbsProps {
  crumbs: Crumb[];
}

// First level, then the ellipsis, then the current page.
const COLLAPSED_PROPS = {
  maxItems: 2,
  itemsBeforeCollapse: 1,
  itemsAfterCollapse: 1
};

type Fit = 'full' | 'collapsed' | 'hidden';

const BreadCrumbs = ({ crumbs }: BreadCrumbsProps) => {
  const title = upFirstLetter(crumbs[crumbs.length - 1].text);
  document.title = `Jarvis - ${title || 'Perimeter81'}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const fullWidthRef = useRef(0);
  const collapsedWidthRef = useRef(0);
  const [fit, setFit] = useState<Fit>('full');

  const crumbsKey = crumbs.map(crumb => crumb.text).join('|');

  // MUI only drops items when there are more than `maxItems`, so a 2-crumb
  // trail cannot be collapsed at all - and collapsing would not shorten it
  // anyway, since "A ... B" is no shorter than "A > B". Those go straight from
  // full to hidden. 24 routes are 2-crumb, every sidebar landing page.
  const canCollapse = crumbs.length > COLLAPSED_PROPS.maxItems;

  // A new trail has its own widths, so re-measure from scratch.
  useLayoutEffect(() => {
    fullWidthRef.current = 0;
    collapsedWidthRef.current = 0;
    setFit('full');
  }, [crumbsKey]);

  // A breakpoint only approximates "does it fit" - it collapsed a short trail
  // at 1280px and left a long one overflowing at 1430px. This measures the
  // real thing.
  //
  // Each width is captured only in the state that actually renders it, and the
  // decision is a cascade over those remembered widths rather than a comparison
  // against whatever is on screen right now. That is what stops it oscillating:
  // narrowing and widening back returns to the same state, because the widths
  // never change while the trail does.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const measure = () => {
      const nav = container.firstElementChild;
      if (!nav) return;
      const available = container.clientWidth;

      // `hidden` keeps the DOM, so it still measures - that is what makes the
      // return path work. It renders the collapsed trail when it can, so the
      // collapsed width stays fresh while hidden.
      if (fit === 'full' || !canCollapse) {
        fullWidthRef.current = nav.scrollWidth;
      } else {
        collapsedWidthRef.current = nav.scrollWidth;
      }

      const nextFit = (): Fit => {
        if (fullWidthRef.current && fullWidthRef.current <= available) {
          return 'full';
        }
        if (!canCollapse) return 'hidden';
        // Collapsed width is unknown until collapsed once. Try it, then
        // re-evaluate on the next pass with a real measurement.
        if (!collapsedWidthRef.current) return 'collapsed';
        return collapsedWidthRef.current <= available ? 'collapsed' : 'hidden';
      };

      const next = nextFit();
      if (next !== fit) setFit(next);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fit, canCollapse, crumbsKey]);

  // The p81 Breadcrumbs whitelists its props, so maxItems cannot be passed in.
  // It does render a real MuiBreadcrumbs from the app's own @mui/material,
  // which the theme can reach. createTheme/ThemeProvider come from
  // @mui/material/styles, NOT @p81-common/ui: the package's ThemeProvider has
  // no `theme` prop, and its createTheme takes only palette/typography and
  // merges into module-level singletons.
  const breadcrumbTheme = useMemo(
    () => (outerTheme: Theme) =>
      createTheme(outerTheme, {
        components: {
          MuiBreadcrumbs: {
            defaultProps: fit === 'full' ? {} : COLLAPSED_PROPS
          }
        }
      }),
    [fit]
  );

  // `data-fit` drives the hiding rule in MainHeader/styles.ts, and doubles as
  // the handle for checking which state is active in the browser.
  return (
    <div ref={containerRef} data-fit={fit}>
      <ThemeProvider theme={breadcrumbTheme}>
        <Breadcrumbs customBreadcrumbs={crumbs} />
      </ThemeProvider>
    </div>
  );
};

export default BreadCrumbs;
